import { RotateCcw } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useChat, type ChatSystemRow as ChatSystemRowData } from "@/contexts/ChatContext";

import {
  ERROR_STOP_FAILED,
  LOGIN_NUDGE_LABEL,
  RETRY_LABEL,
  errorCopyForCode,
  limitRowCopy,
} from "./chatCopy";

/**
 * A row that is NOT the bot talking.
 *
 * A rate refusal and a transport failure are the PLATFORM speaking, and dressing either in ناصح's
 * bubble is precisely the dishonesty this phase's tone target rejects: it makes the product's own
 * limits sound like something the bot decided. So these rows get no bubble, no avatar and no
 * alignment to the bot's side. 13px/400 in `--ys-ink-2`, full width.
 *
 * THEY ARE APPENDED INTO THE THREAD rather than floated over it. A refusal that scrolls away with the
 * conversation is part of what happened; a toast or a banner is an alert about the present that
 * outlives its own moment.
 *
 * THE STOP BUTTON IS NOT DESTRUCTIVE-COLOURED AND NEITHER IS ANY OF THIS. D-08 keeps the partial, so
 * stopping destroys nothing, and no row here is an emergency. The one red thing in this shell is the
 * launcher's unread dot.
 */

/** 13px/400/1.5, the shell's Label role. Every string in this file. */
const ROW_TEXT_STYLE = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.5,
  color: "var(--ys-ink-2)",
} as const;

/**
 * The login nudge (D-18), rendered INLINE inside the refusal's own sentence.
 *
 * `--ys-orange-700` with `underline`, measured 5.33:1. This is the commercially important string in
 * the phase, which is exactly why it does not get the bare primary orange the first design draft gave
 * it (2.71:1): a conversion CTA that some visitors cannot read is not a CTA.
 *
 * The capture goes through the context's own handler, never `posthog.capture` here -- the panel's CTAs
 * appear in several places and the capture belongs in the shared handler. `showAuthModal` then fires
 * `auth_modal_shown` centrally off its typed trigger, so this call site declares intent and nothing
 * more.
 */
const LoginNudge = ({ layer }: { layer: "ip" | "session" | "user" }) => {
  const { showAuthModal } = useAuth();
  const { noteLoginNudgeClicked } = useChat();

  return (
    <button
      type="button"
      onClick={() => {
        noteLoginNudgeClicked(layer);
        showAuthModal({ trigger: "chat_limit" });
      }}
      // `inline-flex` with a 44px minimum inside running prose: the touch target is real without
      // breaking the sentence it lives in.
      className="mx-1 inline-flex min-h-11 items-center rounded-[var(--ys-r-sm)] align-middle underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2"
      style={{ ...ROW_TEXT_STYLE, color: "var(--ys-orange-700)" }}
    >
      {LOGIN_NUDGE_LABEL}
    </button>
  );
};

/** `جرب تاني`, on the two rows where trying again is actually the visitor's move. */
const RetryAction = ({ onRetry }: { onRetry: () => void }) => (
  <button
    type="button"
    onClick={onRetry}
    className="mt-1 flex min-h-11 items-center gap-1 rounded-[var(--ys-r-sm)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2"
    style={ROW_TEXT_STYLE}
  >
    <RotateCcw size={14} strokeWidth={2} aria-hidden="true" />
    {RETRY_LABEL}
  </button>
);

export default function ChatSystemRow({
  row,
  active,
}: {
  row: ChatSystemRowData;
  /**
   * Whether a `limit` row is the one currently in force. The countdown is live state, not a property
   * of a row that was appended thirty seconds ago, and a thread can hold an expired refusal from
   * earlier in the conversation. Only the active one renders.
   */
  active?: boolean;
}) {
  const { countdownSec, retrySend } = useChat();
  const { session } = useAuth();
  const authenticated = (session?.token ?? null) !== null;

  if (row.system === "limit") {
    // BOTH THE ROW AND THE COMPOSER LOCK CLEAR SILENTLY when the window passes. There is no "you can
    // talk again" line, because nobody needs to be told they have been un-punished. `active` going
    // false is that clearing.
    if (active !== true) return null;

    const sentence = limitRowCopy({ authenticated, countdownSec });

    if (authenticated) {
      return (
        <div className="w-full" style={ROW_TEXT_STYLE}>
          {sentence}
        </div>
      );
    }

    // The anonymous variant carries the nudge inside its own sentence, so the split is on the label
    // the copy module already owns rather than on an index this file would have to keep in step.
    const [before, ...rest] = sentence.split(LOGIN_NUDGE_LABEL);
    return (
      <div className="w-full" style={ROW_TEXT_STYLE}>
        {before}
        <LoginNudge layer={row.layer ?? "ip"} />
        {rest.join(LOGIN_NUDGE_LABEL)}
      </div>
    );
  }

  if (row.system === "stop_failed") {
    // NO RETRY BUTTON HERE, and that is the design: retrying an abort that already failed is not a
    // visitor's job. The stop button itself has returned to enabled and a second press retries. The
    // copy says the honest thing -- the bot is in fact still talking, and the composer is still
    // locked because of it.
    return (
      <div className="w-full" style={ROW_TEXT_STYLE}>
        {ERROR_STOP_FAILED}
      </div>
    );
  }

  // `error` (the stream gave up after the reconnect budget) and `send_failed` (the message never
  // left). Both are the visitor's to retry, and `retrySend` re-issues the SAME message key, which is
  // what makes a retry produce one turn rather than two.
  return (
    <div className="flex w-full flex-col items-start" style={ROW_TEXT_STYLE}>
      <span>{errorCopyForCode(row.code)}</span>
      <RetryAction onRetry={retrySend} />
    </div>
  );
}
